import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useState } from "react";

import BottomDrawer from "components/BottomDrawer";
import Button from "components/Button";
import Flex from "components/Flex";
import { CameraIcon } from "components/Icons/Camera";
import InlineError from "components/InlineError";
import PageLayout from "components/PageLayout";
import PhotoGrid from "components/PhotoGrid";
import PhotoUpload from "components/PhotoUpload";
import { Photo, UnUploadedImage } from "components/PhotoUpload/types";
import Text from "components/Text";
import PhotoTips from "components/Tips";

import { clientConfigAtom } from "atoms/clientConfig";
import { damagePhotosAtom, isMinimumPhotoUploadedAtom } from "atoms/damagePhotos";
import { pageDrawerAtom } from "atoms/pageDrawer";
import { useCheckpoint } from "hooks/useCheckpoint";
import { useClientTranslation } from "hooks/useClientTranslation";
import { usePageRouting } from "hooks/usePageRouting";
import { Checkpoint } from "types/Checkpoint";
import { isSelectedImageDuplicate } from "utils/imageProcessing/handleDuplicates";
import { processImageForUpload } from "utils/imageProcessing/processImageForUpload";

import { SAMPLE_PHOTOS } from "./constants";

export const DamagePhotos = () => {
  const targetReached = useCheckpoint(Checkpoint.CLAIM_SUBMITTED);

  const { onTrackAndNext, currentProgressStep, continueCTA } = usePageRouting();
  const { t } = useClientTranslation();

  const {
    damagePhotos: { photoTips },
    detectDuplicatePicture,
  } = useAtomValue(clientConfigAtom);
  const toggleDrawer = useSetAtom(pageDrawerAtom);
  const [damagePhotos, setDamagePhotos] = useAtom(damagePhotosAtom);
  const isMinimumPhotoUploaded = useAtomValue(isMinimumPhotoUploadedAtom);
  const [hasDuplicates, setHasDuplicates] = useState(false);

  const onDrop = (files: File[]) => {
    setHasDuplicates(false);
    const toUpload: UnUploadedImage[] = [...damagePhotos];
    files.map(async (file, index) => {
      const image = await processImageForUpload({
        file,
        alt: `Damage photo to upload number ${index + 1}`,
        type: Photo.Damage,
      });
      if (detectDuplicatePicture && isSelectedImageDuplicate(image, toUpload)) {
        setHasDuplicates(true);
      } else {
        toUpload.push(image);
        setDamagePhotos((photos) => [...photos, image]);
      }
    });
  };

  return (
    <PageLayout
      currentStep={currentProgressStep}
      title={t("damage_photos.header")}
      dataTestId="damagePhotos"
      forceContinue={targetReached}
      bottomButtons={
        <Flex gap={16}>
          <PhotoUpload onDrop={onDrop}>
            <Button typeVariant={isMinimumPhotoUploaded ? "secondary" : "primary"} leftIcon={<CameraIcon />}>
              {t("global.add_photos")}
            </Button>
          </PhotoUpload>
          {isMinimumPhotoUploaded && (
            <Button
              onClick={onTrackAndNext({
                name: "Damage photos",
                properties: { numberOfImages: damagePhotos.length },
              })}
            >
              {continueCTA}
            </Button>
          )}
        </Flex>
      }
    >
      <Flex mx={24} mb={32} my={8} gap={16}>
        <Text sizeVariant="md">{t("damage_photos.instruction")}</Text>

        <PhotoTips tips={photoTips} />

        {Boolean(damagePhotos.length) && (
          <Button typeVariant="link" style={{ alignSelf: "flex-start" }} onClick={toggleDrawer}>
            {t("global.view_examples")}
          </Button>
        )}

        {damagePhotos.length ? (
          <PhotoGrid images={damagePhotos} deleteFromAtom={setDamagePhotos} />
        ) : (
          <PhotoGrid images={SAMPLE_PHOTOS} />
        )}

        {hasDuplicates && <InlineError variant="damagePhotos" />}
      </Flex>
      <BottomDrawer>
        <PhotoGrid images={SAMPLE_PHOTOS} />
      </BottomDrawer>
    </PageLayout>
  );
};
